import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Bell, BellOff } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

export default function ClientChat({ getAuthHeader }) {
  const [open, setOpen] = useState(() => {
    // Always open by default (desktop); stays closed only if the client closed it this session
    if (sessionStorage.getItem('2k2day_chat_closed') === '1') return false;
    return window.innerWidth >= 768;
  });
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [chatStatus, setChatStatus] = useState(null);
  const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const bottomRef = useRef(null);
  const openRef = useRef(false);
  openRef.current = open;

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/client/chat`, getAuthHeader());
      setMessages(res.data);
      setUnread(0);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  // Poll: unread badge when closed, full thread when open
  useEffect(() => {
    const poll = async () => {
      if (openRef.current) { fetchMessages(); return; }
      try {
        const res = await axios.get(`${API_URL}/client/chat/unread-count`, getAuthHeader());
        setUnread(res.data.count || 0);
      } catch { /* ignore */ }
    };
    poll();
    const interval = setInterval(poll, 12000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (open) fetchMessages(); }, [open, fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  // Check existing push subscription
  useEffect(() => {
    if (!pushSupported) return;
    navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription())
      .then(sub => setPushEnabled(!!sub && Notification.permission === 'granted'))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Office hours / online status
  useEffect(() => {
    axios.get(`${API_URL}/chat/status`, getAuthHeader())
      .then(res => setChatStatus(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePush = async () => {
    if (!pushSupported) { toast.error('Notifications not supported on this browser'); return; }
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await axios.post(`${API_URL}/client/push/unsubscribe`, { endpoint: sub.endpoint }, getAuthHeader());
          await sub.unsubscribe();
        }
        setPushEnabled(false);
        toast.success('Notifications turned off');
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notifications blocked — allow them in your browser settings');
        return;
      }
      const { data } = await axios.get(`${API_URL}/push/vapid-public-key`);
      if (!data.key) { toast.error('Notifications not configured'); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await axios.post(`${API_URL}/client/push/subscribe`, sub.toJSON(), getAuthHeader());
      setPushEnabled(true);
      toast.success("Notifications on — we'll ping you when we reply");
    } catch (e) {
      toast.error('Could not enable notifications');
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/client/chat`, { message: text }, getAuthHeader());
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch { /* keep input so user can retry */ }
    finally { setSending(false); }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => { sessionStorage.removeItem('2k2day_chat_closed'); setOpen(true); }}
          data-testid="client-chat-open-btn"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#0f2b4a] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Chat with us"
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[11px] font-bold px-1 animate-pulse" data-testid="client-chat-unread-badge">{unread}</span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-[380px] h-[480px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="client-chat-panel">
          <div className="bg-[#0f2b4a] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <div>
                <p className="font-semibold text-sm leading-tight flex items-center gap-1.5">
                  2k2day Support
                  <span className={`inline-block w-2 h-2 rounded-full ${chatStatus?.online === false ? 'bg-amber-400' : 'bg-emerald-400'}`} data-testid="chat-status-dot"></span>
                </p>
                <p className="text-[11px] text-white/60">
                  {chatStatus?.online === false ? `Away — back ${chatStatus.hours_label}` : 'Message us here — no email or WhatsApp needed'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {pushSupported && (
                <button onClick={togglePush} className="p-1.5 hover:bg-white/10 rounded" data-testid="client-chat-notify-btn"
                  title={pushEnabled ? 'Turn off notifications' : 'Get notified on your phone when we reply'}>
                  {pushEnabled ? <Bell className="w-4 h-4 text-emerald-300" /> : <BellOff className="w-4 h-4 text-white/60" />}
                </button>
              )}
              <button onClick={() => { sessionStorage.setItem('2k2day_chat_closed', '1'); setOpen(false); }} className="p-1 hover:bg-white/10 rounded" data-testid="client-chat-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="client-chat-messages">
            {messages.length === 0 && (
              <p className="text-center text-slate-400 text-sm mt-10">
                Hi! Send us a message about your loan or application and our team will reply here.
              </p>
            )}
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-[11px] text-blue-700" data-testid="client-chat-note">
              💬 Please use this chat for all communication about your loan — questions, documents, DebiCheck or payouts. Our team replies right here and you'll be notified.
            </div>
            {chatStatus?.online === false && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="chat-away-note">
                🌙 {chatStatus.away_message || "We're currently offline — leave your message and we'll reply during office hours."}
                <span className="block mt-0.5 font-semibold">Office hours: {chatStatus.hours_label}</span>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.sender === 'client' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === 'client'
                  ? 'bg-[#0f2b4a] text-white rounded-br-sm'
                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
                  {m.sender === 'staff' && <p className="text-[10px] font-semibold text-emerald-600 mb-0.5">{m.sender_name || '2k2day'}</p>}
                  <p className="whitespace-pre-wrap break-words">{m.message}</p>
                  <p className={`text-[10px] mt-1 ${m.sender === 'client' ? 'text-white/50' : 'text-slate-400'}`}>
                    {new Date(m.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="p-2.5 border-t border-slate-100 bg-white flex items-center gap-2">
            <input
              className="flex-1 text-sm border border-slate-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f2b4a]/30"
              placeholder="Type a message..."
              value={input}
              maxLength={2000}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              data-testid="client-chat-input"
            />
            <Button size="icon" className="rounded-full bg-[#0f2b4a] shrink-0" disabled={!input.trim() || sending} onClick={send} data-testid="client-chat-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
